import React from 'react';
import { normaliseDeliveryPickupConfig, normaliseFormDeliveryPickupValues } from '../../util/formValidationHelpers';

/**
 * Bolds the quantity/duration phrase within a disclaimer message (e.g. "72+ hours",
 * "50%", "3+ successful rentals"), matching Figma's "Free cancellation **72 hours**
 * before pickup" pattern - without touching getCancellationMessage/getVerificationMessage
 * or the translation catalog. Presentation-only: returns the original string unchanged
 * when no such phrase is found (e.g. "No refund after booking is confirmed").
 *
 * @param {string} text - output of getCancellationMessage/getVerificationMessage
 */
export function emphasizeDisclaimerQuantity(text) {
  if (typeof text !== 'string') {
    return text;
  }
  // Non-capturing: String.split() splices a capturing group's matches into its output,
  // which desynchronises parts[i] from matches[i] below and reorders the sentence
  // ("50% refund with 72+ hours notice" rendered as "50%72+ hours refund with hours
  // notice"). The group is only here for grouping, so it must not capture.
  const pattern = /\d+\+?%?(?:\s*(?:hours?|successful rentals?))?/gi;
  const matches = text.match(pattern);
  if (!matches) {
    return text;
  }
  const parts = text.split(pattern);
  const nodes = [];
  parts.forEach((part, i) => {
    if (part) {
      nodes.push(part);
    }
    if (matches[i] != null) {
      nodes.push(<strong key={i}>{matches[i]}</strong>);
    }
  });
  return nodes;
}

/**
 * Groups additional fees into:
 * - usageMultipleCharges
 * - otherOptionalCharges
 * - otherRequiredCharges
 *
 * @param {Array} fees - convertedAdditionalFees array
 * @param {string} USAGE_MULTIPLE
 * @param {string} REQUIRED_UPFRONT_OPTIONAL
 * @param {string} REQUIRED_UPFRONT
 */
export function groupAdditionalFees(
  fees,
  USAGE_MULTIPLE,
  REQUIRED_UPFRONT_OPTIONAL,
  REQUIRED_UPFRONT
) {
  const usageMultipleCharges = [];
  const otherOptionalCharges = [];
  const otherRequiredCharges = [];

  fees?.forEach(fee => {
    if (fee.feeCalculationType === USAGE_MULTIPLE) {
      usageMultipleCharges.push(fee);
    } else if (fee.whenToApply === REQUIRED_UPFRONT_OPTIONAL) {
      otherOptionalCharges.push(fee);
    } else if (fee.whenToApply === REQUIRED_UPFRONT) {
      otherRequiredCharges.push(fee);
    }
  });

  return {
    usageMultipleCharges,
    otherOptionalCharges,
    otherRequiredCharges,
  };
}


/**
* Extracts delivery & pickup fee details and availability:
*
* Returns:
* - isDeliveryRequired (boolean)
* - isPickupRequired (boolean)
* - hasDeliveryOption (boolean)
* - hasPickupOption (boolean)
*
* @param {Array} fees - convertedAdditionalFees array
* @param {string} DELIVERY_FEE
* @param {string} PICKUP_FEE
* @param {string} REQUIRED_UPFRONT
*/
export function extractDeliveryPickupInfo(
  fees,
  DELIVERY_FEE,
  PICKUP_FEE,
  REQUIRED_UPFRONT
) {
  let isDeliveryRequired = false;
  let isPickupRequired = false;
  let hasDeliveryOption = false;
  let hasPickupOption = false;

  fees?.forEach(fee => {
    if (fee.feeType === DELIVERY_FEE) {
      hasDeliveryOption = true;
      if (fee.whenToApply === REQUIRED_UPFRONT) {
        isDeliveryRequired = true;
      }
    }

    if (fee.feeType === PICKUP_FEE) {
      hasPickupOption = true;
      if (fee.whenToApply === REQUIRED_UPFRONT) {
        isPickupRequired = true;
      }
    }
  });

  return {
    isDeliveryRequired,
    isPickupRequired,
    hasDeliveryOption,
    hasPickupOption,
  };
}

/**
 * Filters fees to separate delivery/pickup fees from other fees
 *
 * @param {Array} fees - Array of fee objects
 * @param {string} DELIVERY_FEE - Constant for delivery fee type
 * @param {string} PICKUP_FEE - Constant for pickup fee type
 * @returns {Object} - Object with deliveryPickupFees and otherFees arrays
 */
export function filterDeliveryPickupFees(fees, DELIVERY_FEE, PICKUP_FEE) {
  const deliveryPickupFees = [];
  const otherFees = [];

  fees?.forEach(fee => {
    if (fee.feeType === DELIVERY_FEE || fee.feeType === PICKUP_FEE) {
      deliveryPickupFees.push(fee);
    } else {
      otherFees.push(fee);
    }
  });

  return {
    deliveryPickupFees,
    otherFees,
  };
}

/**
 * Separates fees into delivery/pickup and other fees for organized display
 *
 * @param {Object} groupedFees - Object from groupAdditionalFees()
 * @param {string} DELIVERY_FEE - Constant for delivery fee type
 * @param {string} PICKUP_FEE - Constant for pickup fee type
 * @returns {Object} - Object with separated fee arrays
 */
export function separateDeliveryPickupFees(groupedFees, DELIVERY_FEE, PICKUP_FEE) {
  const {
    usageMultipleCharges = [],
    otherOptionalCharges = [],
    otherRequiredCharges = [],
  } = groupedFees;

  // Separate usage multiple charges
  const {
    deliveryPickupFees: deliveryPickupUsageCharges,
    otherFees: otherUsageCharges,
  } = filterDeliveryPickupFees(usageMultipleCharges, DELIVERY_FEE, PICKUP_FEE);

  // Separate optional charges
  const {
    deliveryPickupFees: deliveryPickupOptionalCharges,
    otherFees: otherOptionalChargesFiltered,
  } = filterDeliveryPickupFees(otherOptionalCharges, DELIVERY_FEE, PICKUP_FEE);

  // Separate required charges
  const {
    deliveryPickupFees: deliveryPickupRequiredCharges,
    otherFees: otherRequiredChargesFiltered,
  } = filterDeliveryPickupFees(otherRequiredCharges, DELIVERY_FEE, PICKUP_FEE);

  return {
    // Delivery/Pickup fees
    deliveryPickupUsageCharges,
    deliveryPickupOptionalCharges,
    deliveryPickupRequiredCharges,
    // Other fees (for Add-ons section)
    otherUsageCharges,
    otherOptionalChargesFiltered,
    otherRequiredChargesFiltered,
  };
}

/**
 * Organizes all fees into clear categories for component consumption.
 * This consolidates grouping, separation, and extraction into one clear structure.
 * 
 * @param {Array} fees - convertedAdditionalFees array
 * @param {string} DELIVERY_FEE - Constant for delivery fee type
 * @param {string} PICKUP_FEE - Constant for pickup fee type
 * @param {string} USAGE_MULTIPLE - Constant for usage multiple calculation type
 * @param {string} REQUIRED_UPFRONT_OPTIONAL - Constant for optional required upfront
 * @param {string} REQUIRED_UPFRONT - Constant for required upfront
 * @returns {Object} Organized fee structure with deliveryPickup and addons objects
 */
export function organizeFees(
  fees,
  DELIVERY_FEE,
  PICKUP_FEE,
  USAGE_MULTIPLE,
  REQUIRED_UPFRONT_OPTIONAL,
  REQUIRED_UPFRONT
) {
  // First, group by calculation type
  const grouped = groupAdditionalFees(
    fees,
    USAGE_MULTIPLE,
    REQUIRED_UPFRONT_OPTIONAL,
    REQUIRED_UPFRONT
  );

  // Extract delivery/pickup info
  const deliveryPickupInfo = extractDeliveryPickupInfo(
    fees,
    DELIVERY_FEE,
    PICKUP_FEE,
    REQUIRED_UPFRONT
  );

  // Separate delivery/pickup from other fees
  const separated = separateDeliveryPickupFees(
    grouped,
    DELIVERY_FEE,
    PICKUP_FEE
  );

  // Combine all delivery/pickup fees to find the actual fee objects
  const allDeliveryPickupFees = [
    ...separated.deliveryPickupUsageCharges,
    ...separated.deliveryPickupOptionalCharges,
    ...separated.deliveryPickupRequiredCharges,
  ];

  const deliveryFee = allDeliveryPickupFees.find(f => f.feeType === DELIVERY_FEE) || null;
  const pickupFee = allDeliveryPickupFees.find(f => f.feeType === PICKUP_FEE) || null;

  // Derive required flags from fee objects or extracted info
  const isDeliveryRequired = deliveryFee?.whenToApply === REQUIRED_UPFRONT || deliveryPickupInfo.isDeliveryRequired;
  const isPickupRequired = pickupFee?.whenToApply === REQUIRED_UPFRONT || deliveryPickupInfo.isPickupRequired;

  return {
    // For DeliveryPickupOptionsField - consolidated delivery/pickup data
    deliveryPickup: {
      deliveryFee,
      pickupFee,
      hasDeliveryOption: deliveryPickupInfo.hasDeliveryOption,
      hasPickupOption: deliveryPickupInfo.hasPickupOption,
      isDeliveryRequired,
      isPickupRequired,
    },

    // For Add-ons section - only non-delivery/pickup fees
    addons: {
      usageMultipleCharges: separated.otherUsageCharges,
      optionalCharges: separated.otherOptionalChargesFiltered,
      requiredCharges: separated.otherRequiredChargesFiltered,
    },
  };
}


/**
 * Whether the Receiving Item / Returning Item fulfillment methods have been
 * selected, given the current form values. Shared by BookingDatesForm and
 * BookingTimeForm's FormSpy error-clearing handler and their Rent Now
 * button's disabled/tooltip state, so both agree on the same definition.
 *
 * @param {Object} values - current form values
 * @param {Object} deliveryPickup - Delivery/pickup configuration object
 * @param {string} DELIVERY_FEE - Constant for delivery fee type
 * @param {string} PICKUP_FEE - Constant for pickup fee type
 * @param {string} USAGE_MULTIPLE - Constant for usage multiple calculation type
 */
export function computeFulfillmentStatus(values, deliveryPickup, DELIVERY_FEE, PICKUP_FEE, USAGE_MULTIPLE) {
  const {
    deliveryFee: deliveryFeeObj,
    pickupFee: pickupFeeObj,
    isDeliveryRequired,
    isPickupRequired,
  } = deliveryPickup || {};

  const hasDeliveryViaUsageMultiple =
    deliveryFeeObj?.feeCalculationType === USAGE_MULTIPLE && values[DELIVERY_FEE] > 0;
  const hasDeliveryViaOptional = values.otherOptionalFees?.includes(DELIVERY_FEE);
  const hasSelfPickup = values.pickupAtOwnerLocation?.[0] === true;

  const hasReturnViaUsageMultiple =
    pickupFeeObj?.feeCalculationType === USAGE_MULTIPLE && values[PICKUP_FEE] > 0;
  const hasReturnViaOptional = values.otherOptionalFees?.includes(PICKUP_FEE);
  const hasSelfReturn = values.returnAtOwnerLocation?.[0] === true;

  const hasDeliveryMethod =
    hasDeliveryViaUsageMultiple || hasDeliveryViaOptional || !!isDeliveryRequired || hasSelfPickup;
  const hasReturnMethod =
    hasReturnViaUsageMultiple || hasReturnViaOptional || !!isPickupRequired || hasSelfReturn;

  return { hasDeliveryMethod, hasReturnMethod };
}

/**
 * Validates delivery and pickup method selection.
 * Supports both CartModal and OrderPanel form structures.
 * 
 * @param {Object} deliveryPickup - Delivery/pickup configuration object
 * @param {Object} values - Form values containing delivery/pickup selections
 * @param {string} DELIVERY_FEE - Constant for delivery fee type
 * @param {string} PICKUP_FEE - Constant for pickup fee type
 * @param {string} USAGE_MULTIPLE - Constant for usage multiple calculation type
 * @param {boolean} isCartModal - Whether the form values are from CartModal (default: false)
 * @returns {Object} - Validation result with isValid flag and error type if invalid
 */
export function validateDeliveryPickupSelection(
  deliveryPickup,
  values,
  DELIVERY_FEE,
  PICKUP_FEE,
  USAGE_MULTIPLE,
  isCartModal = false,
  allowOptionalNoSelection = false
) {
  // Normalize values if from CartModal
  const normalizedValues = isCartModal
    ? normaliseFormDeliveryPickupValues(values, true)
    : values;


  const {
    deliveryFee: deliveryFeeObj,
    pickupFee: pickupFeeObj,
    isDeliveryRequired,
    isPickupRequired,
    hasDeliveryOption,
    hasPickupOption,
  } = isCartModal ? normaliseDeliveryPickupConfig(deliveryPickup) : deliveryPickup;

  // Only validate if delivery/pickup options exist
  const hasDeliveryOrPickupOptions = hasDeliveryOption || hasPickupOption;

  if (!hasDeliveryOrPickupOptions) {
    return { isValid: true, error: null };
  }

  // Check DELIVERY method selection (how item gets to renter)
  const hasDeliveryViaUsageMultiple =
    deliveryFeeObj?.feeCalculationType === USAGE_MULTIPLE &&
    normalizedValues[DELIVERY_FEE] > 0;
  const hasDeliveryViaOptional = normalizedValues.otherOptionalFees?.includes(
    DELIVERY_FEE
  );
  const hasDeliveryViaRequired = !!isDeliveryRequired;
  const hasSelfPickup = normalizedValues.pickupAtOwnerLocation?.[0] === true;
  const anyItemHasDelivery = normalizedValues.anyItemHasDelivery;

  const hasDeliveryMethod =
    hasDeliveryViaUsageMultiple ||
    hasDeliveryViaOptional ||
    hasDeliveryViaRequired ||
    hasSelfPickup ||
    anyItemHasDelivery;

  // Check RETURN method selection (how item gets back to owner)
  const hasReturnViaUsageMultiple =
    pickupFeeObj?.feeCalculationType === USAGE_MULTIPLE &&
    normalizedValues[PICKUP_FEE] > 0;
  const hasReturnViaOptional = normalizedValues.otherOptionalFees?.includes(
    PICKUP_FEE
  );
  const hasReturnViaRequired = !!isPickupRequired;
  const hasSelfReturn = normalizedValues.returnAtOwnerLocation?.[0] === true;
  const anyItemHasPickup = normalizedValues.anyItemHasPickup;
  const hasReturnMethod =
    hasReturnViaUsageMultiple ||
    hasReturnViaOptional ||
    hasReturnViaRequired ||
    hasSelfReturn ||
    anyItemHasPickup;

  const missingDeliveryMethod = !hasDeliveryMethod;
  const missingReturnMethod = !hasReturnMethod;
  const deliverySelectionRequired = !!isDeliveryRequired;
  const returnSelectionRequired = !!isPickupRequired;
  const missingRequiredDelivery = deliverySelectionRequired && missingDeliveryMethod;
  const missingRequiredReturn = returnSelectionRequired && missingReturnMethod;

  // Required paths must still block progression.
  if (missingRequiredDelivery) {
    return { isValid: false, error: 'delivery', warning: null };
  }
  if (missingRequiredReturn) {
    return { isValid: false, error: 'return', warning: null };
  }

  // Optional paths: allow progression but provide warning context for UI.
  if (allowOptionalNoSelection && (missingDeliveryMethod || missingReturnMethod)) {
    return {
      isValid: true,
      error: null,
      warning: missingDeliveryMethod ? 'delivery' : 'return',
    };
  }

  // Legacy strict mode (e.g. cart modal): block if either method is missing.
  if (missingDeliveryMethod) {
    return { isValid: false, error: 'delivery', warning: null };
  }
  if (missingReturnMethod) {
    return { isValid: false, error: 'return', warning: null };
  }

  return { isValid: true, error: null, warning: null };
}
